import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Building2,
    Users,
    BadgePercent,
    Briefcase,
    LineChart,
    FileSpreadsheet,
    CalendarClock,
    History,
    ShieldCheck,
    ShieldAlert,
    Palette,
    Globe2,
    LogOut,
    Shield,
    UserCog,
    Layers,
    FileStack,
    Mail,
    RefreshCw,
    BellRing,
} from "lucide-react";
import { useAuth, hasRole } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { api } from "../lib/api";

const internalCheck = (u) =>
    u.role === "super_admin" || (u.role === "admin" && u.can_view_actual_cost);

const itemClass = ({ isActive }) =>
    `group flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-200 border-l-2 ${
        isActive
            ? "wl-accent-text bg-zinc-100"
            : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 border-transparent"
    }`;

const activeStyle = ({ isActive }) =>
    isActive
        ? { borderLeftColor: "var(--wl-primary-hex)" }
        : { borderLeftColor: "transparent" };

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { tenant } = useBranding();
    const navigate = useNavigate();
    // Effective allowed nav keys for the current user (filtered against role + agency/advertiser overrides)
    const [allowedKeys, setAllowedKeys] = useState(null); // null = loading; [] = none; list = filter

    useEffect(() => {
        if (!user?.id) return undefined;
        let cancelled = false;

        const fetchPerms = () => {
            api.get("/nav/permissions/me")
                .then((r) => { if (!cancelled) setAllowedKeys(r.data?.allowed_keys || []); })
                .catch(() => { if (!cancelled) setAllowedKeys([]); });
        };
        fetchPerms();

        // Real-time push — open an SSE stream so saved permission changes propagate
        // without a page refresh. EventSource auto-reconnects on transient drops.
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        const token = (typeof window !== "undefined" && window.localStorage)
            ? window.localStorage.getItem("token") : null;
        if (!token) return undefined;
        const url = `${apiBase}/api/nav/permissions/stream?token=${encodeURIComponent(token)}`;
        // Note: EventSource can't send custom headers — we accept the token in a query param too.
        let es = null;
        try {
            es = new EventSource(url, { withCredentials: false });
            es.addEventListener("permissions-updated", () => { if (!cancelled) fetchPerms(); });
            es.addEventListener("ready", () => { /* connection live */ });
            es.onerror = () => { /* EventSource auto-reconnects — no toast needed */ };
        } catch (e) { void e; }
        return () => {
            cancelled = true;
            if (es) es.close();
        };
    }, [user?.id]);

    if (!user) return null;

    const brandName =
        tenant?.branding?.brand_name ||
        tenant?.agency?.name ||
        (user.role === "super_admin" ? "Platform HQ" : "Workspace");

    const onLogout = async () => {
        await logout();
        navigate("/login");
    };

    const links = [
        { key: "dashboard",        to: "/dashboard",        icon: LayoutDashboard, label: "Dashboard",        show: true },
        { key: "agencies",         to: "/agencies",         icon: Building2,       label: "Agencies",         show: hasRole(user, "super_admin") },
        { key: "advertisers",      to: "/advertisers",      icon: Users,           label: "Advertisers",      show: hasRole(user, "super_admin", "admin") },
        { key: "users",            to: "/users",            icon: UserCog,         label: "Users",            show: hasRole(user, "super_admin", "admin") },
        { key: "ad-accounts",      to: "/ad-accounts",      icon: Briefcase,       label: "Ad Accounts",      show: true },
        { key: "campaigns",        to: "/campaigns",        icon: BadgePercent,    label: "Campaigns",        show: true },
        { key: "detailed-reports", to: "/detailed-reports", icon: Layers,          label: "Detailed Reports", show: true },
        { key: "templates",        to: "/templates",        icon: FileStack,       label: "Templates",        show: true },
        { key: "schedules",        to: "/schedules",        icon: CalendarClock,   label: "Schedules",        show: true },
        { key: "exports",          to: "/exports",          icon: History,         label: "Export History",   show: true },
        { key: "margin-alerts",    to: "/margin-alerts",    icon: ShieldAlert,     label: "Margin Alerts",    show: internalCheck(user) },
        { key: "alerts",           to: "/alerts",           icon: BellRing,        label: "Alerts",           show: true },
        { key: "branding",         to: "/branding",         icon: Palette,         label: "Branding",         show: hasRole(user, "super_admin", "admin") },
        { key: "domains",          to: "/domains",          icon: Globe2,          label: "Domains",          show: hasRole(user, "super_admin", "admin") },
        { key: "audit-logs",       to: "/audit-logs",       icon: ShieldCheck,     label: "Audit Logs",       show: hasRole(user, "super_admin", "admin") },
        { key: "email-settings",   to: "/settings/email",   icon: Mail,            label: "Email Settings",   show: hasRole(user, "super_admin") },
        { key: "data-sync",        to: "/data-sync",        icon: RefreshCw,       label: "Data Sync",        show: hasRole(user, "super_admin") },
    ];

    // Two-stage visibility:
    //   1. Role-based `show` flag (existing behaviour — covers basic role gating)
    //   2. Backend-driven `allowed_keys` from /nav/permissions/me — super_admin's per-agency
    //      grant + admin's per-advertiser grant. We wait for the API to resolve before rendering
    //      ANY items so the user never sees role-default links flash then disappear.
    const visibleLinks = allowedKeys === null
        ? []  // loading state — render nothing (skeleton below)
        : links.filter((l) => l.show && allowedKeys.includes(l.key));
    const isLoadingPerms = allowedKeys === null;

    return (
        <aside
            className="w-64 shrink-0 bg-zinc-50 border-r border-zinc-200 flex flex-col h-screen sticky top-0"
            data-testid="app-sidebar"
        >
            <div className="px-5 py-5 border-b border-zinc-200 flex items-center gap-3">
                {tenant?.branding?.logo_url ? (
                    <img
                        src={tenant.branding.logo_url}
                        alt={brandName}
                        className="h-9 w-9 rounded-md object-contain bg-white border border-zinc-200"
                        data-testid="sidebar-brand-logo"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ) : (
                    <div
                        className="h-9 w-9 rounded-md flex items-center justify-center"
                        style={{
                            background: "var(--wl-primary-hex)",
                            color: "#ffffff",
                        }}
                        data-testid="sidebar-brand-logo-fallback"
                    >
                        <Shield className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                )}
                <div className="min-w-0">
                    <div className="text-[10px] overline">Tenant</div>
                    <div
                        className="text-sm font-semibold text-zinc-900 truncate font-display"
                        data-testid="sidebar-brand-name"
                    >
                        {brandName}
                    </div>
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-3">
                <div className="overline px-5 mb-2 mt-1">Navigation</div>
                {isLoadingPerms ? (
                    // Skeleton placeholders while /nav/permissions/me is resolving — prevents
                    // role-default items from flashing then disappearing once the override loads.
                    <div className="px-5 py-2 space-y-2" aria-hidden data-testid="sidebar-skeleton">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="h-4 w-4 rounded bg-zinc-200 animate-pulse" />
                                <div className="h-3 rounded bg-zinc-200 animate-pulse" style={{ width: `${50 + ((i * 13) % 35)}%` }} />
                            </div>
                        ))}
                    </div>
                ) : (
                    visibleLinks.map((l) => {
                        const Icon = l.icon;
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                className={itemClass}
                                style={activeStyle}
                                data-testid={`nav-${l.to.replace(/\//g, "-").slice(1)}`}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{l.label}</span>
                            </NavLink>
                        );
                    })
                )}
            </nav>

            <div className="px-4 py-3 border-t border-zinc-200">
                <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-semibold text-zinc-800">
                        {user.full_name?.[0] || user.email[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div
                            className="text-xs text-zinc-800 truncate"
                            data-testid="sidebar-user-name"
                        >
                            {user.full_name}
                        </div>
                        <div className="text-[10px] text-zinc-500 truncate">
                            {user.email}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-100 border border-zinc-200 text-zinc-700"
                        data-testid="sidebar-role-badge"
                    >
                        {user.role.replace("_", " ")}
                    </span>
                    <button
                        onClick={onLogout}
                        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
                        data-testid="logout-btn"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Sign out
                    </button>
                </div>
            </div>
        </aside>
    );
}
